import { Button, Form, Modal, Spin, Switch, message } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import TableComponent from "../../components/TableComponent/TableComponent";
import { getApi, postApi } from "../../utils/apiServices";
import { ADD_HSN, ENABLE_HSN, GET_GST, UPDATE_HSN } from "../../utils/apiPath";
import ToastHelper from "../../services/ToastHelper";
import FormInput from "../../components/FormInputs/FormInput";

const GSTHSN = () => {
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [isLoading, setIsLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const showModal = () => {
    setIsModalOpen(!isModalOpen);
    setEditData(null);
    if (!isModalOpen) form.resetFields();
  };

  const handleAdd = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      setIsLoading(true);

      const payload = {
        gstId: editData ? editData?.gstId : undefined,
        hsnCode: values?.hsnCode,
        description: values?.description,
        gstRate: values?.gstRate,
      };
      const res = await postApi(`${editData ? UPDATE_HSN : ADD_HSN}`, payload);
      setIsLoading(false);
      showModal();
      fetchData();
      message.success(
        `HSN code ${editData ? "updated" : "added"} successfully`
      );
      form.resetFields();
    } catch (error) {
      console.error("Validation Error:", error);
    }
  };

  const columns = [
    {
      title: "HSN code",
      dataIndex: "hsnCode",
    },
    {
      title: "Description",
      dataIndex: "description",
    },
    {
      title: "GST Rate",
      dataIndex: "gstRate",
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <Switch
            checked={record?.isActive}
            onChange={() => handleEnable(record)}
          />
          <Button onClick={() => handleEdit(record)}>🏀 Edit</Button>
        </div>
      ),
    },
  ];

  const fetchGsts = async () => {
    const payload = {
      pageIndex: pagination.current - 1,
      pageSize: pagination.pageSize,
      searchString: "",
    };
    try {
      const res = await postApi(GET_GST, payload);

      if (res?.data?.length !== 0) {
        // ToastHelper.success("success");
        return res;
      } else {
        ToastHelper.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      // ToastHelper.error(
      //   "Failed to connect to the server. Please try again later."
      // );
    } finally {
      setIsLoading(false);
    }
    return null;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const result = await fetchGsts();
    setData(result?.data);
    setPagination((prev) => ({ ...prev, total: result?.totalRecords }));
    setLoading(false);
  }, [pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleEdit = (record) => {
    showModal();
    setEditData(record);
  };

  const handleEnable = async (record) => {
    setIsLoading(true);
    try {
      const res = await getApi(`${ENABLE_HSN}?gstId=${record?.gstId}`);

      if (res?.statusCode === 200) {
        ToastHelper.success("success");
        fetchData();
        return res?.data;
      } else {
        ToastHelper.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      // ToastHelper.error(
      //   "Failed to connect to the server. Please try again later."
      // );
    } finally {
      setIsLoading(false);
    }
    return null;
  };

  useEffect(() => {
    if (editData) {
      form.setFieldsValue({
        hsnCode: editData?.hsnCode,
        description: editData?.description,
        gstRate: editData?.gstRate,
      });
    }
  }, [editData]);

  return (
    <div className="categories">
      <div className="categories_container">
        <div className="categories_header">
          <div className="categories_header_left">
            <span className="active">HSN</span>
          </div>
          <div className="categories_header_right">
            <Button type="primary" onClick={showModal}>
              Add HSN
            </Button>
          </div>
        </div>
        <TableComponent
          columns={columns}
          data={data}
          pagination={pagination}
          loading={loading}
          onTableChange={handleTableChange}
        />
      </div>
      <Modal
        title="Add HSN"
        open={isModalOpen}
        onOk={handleAdd}
        onCancel={showModal}
        centered={true}
        maskClosable={false}
      >
        <Spin spinning={isLoading}>
          <div className="category-topic_modal">
            <Form form={form} layout="vertical">
              <FormInput
                label="HSN code"
                name="hsnCode"
                rules={[
                  {
                    required: true,
                    message: "Please enter HSN code",
                  },
                  {
                    pattern: /^\d{4}(\d{2})?(\d{2})?$/,
                    message: "HSN code must be 4, 6, or 8 digits",
                  },
                ]}
                placeholder="Enter HSN code"
              />
              <FormInput
                label="HSN Description"
                name="description"
                rules={[
                  {
                    required: true,
                    message: "Please enter HSN description",
                  },
                ]}
                placeholder="Enter HSN description"
              />
              <FormInput
                label="GST rate"
                name="gstRate"
                rules={[
                  {
                    required: true,
                    message: "Please enter GST rate",
                  },
                  {
                    validator: (_, value) => {
                      const num = Number(value);
                      if (!value) {
                        return Promise.resolve();
                      }

                      if (!/^\d{1,2}$/.test(value)) {
                        return Promise.reject("Enter a valid number (0–28)");
                      }

                      if (num > 28) {
                        return Promise.reject(
                          "GST rate cannot be more than 28"
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
                maxLength={2}
                placeholder="Enter GST rate"
              />
            </Form>
          </div>
        </Spin>
      </Modal>
    </div>
  );
};

export default GSTHSN;
